import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/ToastContainer';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import HomeNew from './components/HomeNew';
import OnboardingWrapper from './components/OnboardingWrapper';
import ChatPage from './components/ChatPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Bookings from './pages/Bookings';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import WhatsNew from './pages/WhatsNew';
import OurImpact from './pages/OurImpact';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403 (auth errors)
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

function App() {
  useEffect(() => {
    // Disable browser scroll restoration to prevent conflicts
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <Router>
            <ScrollToTop />
            <div className="App">
                <Routes>
              {/* Public routes with layout */}
              <Route path="/" element={<Layout><HomeNew /></Layout>} />
              <Route path="/whats-new" element={<Layout><WhatsNew /></Layout>} />
              <Route path="/our-impact" element={<Layout><OurImpact /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
              
              {/* Auth routes without layout */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Subscription routes without layout */}
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
              
              {/* Protected routes with layout */}
              <Route 
                path="/dashboard" 
                element={
                  <Layout>
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  </Layout>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <Layout>
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  </Layout>
                } 
              />
              <Route
                path="/analytics"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path="/bookings"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <Bookings />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <Layout>
                    <ProtectedRoute>
                      <OnboardingWrapper />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              
              {/* Chat page without layout for clean experience */}
              <Route path="/chat/:handle" element={<ChatPage />} />
            </Routes>
          </div>
        </Router>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <VercelAnalytics />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
